// pages/PlanosPagamento/VincularEstudantePage.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import planosPagamentoService from '../../services/planosPagamentoService';
import api from '../../services/api';
import { formatarMoeda } from '../../utils/formatters';

const VincularEstudantePage = () => {
  const { planoId } = useParams();
  const navigate = useNavigate();

  const [planoBase, setPlanoBase] = useState(null);
  const [estudantes, setEstudantes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    estudanteId: '',
    valorTotal: '',
    observacoes: ''
  });

  // Carregar dados do plano base e estudantes ativos
  useEffect(() => {
    const carregarDados = async () => {
      try {
        console.log('Carregando plano:', planoId);
        setLoading(true);

        // Obter plano de pagamento
        const responsePlano = await planosPagamentoService.obterPlanoPagamento(planoId);
        console.log('Dados do plano recebidos:', responsePlano);
        const plano = responsePlano.data;

        // Verificar se o plano é válido e do tipo BASE
        if (!plano || plano.tipo.toUpperCase() !== 'BASE') {
          setError('Apenas planos base podem ser usados para vincular estudantes');
          return;
        }

        setPlanoBase(plano);
        setFormData(prev => ({
          ...prev,
          valorTotal: plano.valorTotal
        }));

        // Carregar estudantes ativos da instituição
        const instituicaoId = plano.instituicaoId;
        if (instituicaoId) {
          const responseEstudantes = await api.get(`/instituicoes/${instituicaoId}/estudantes-ativos`);
          console.log('Estudantes ativos:', responseEstudantes.data);
          setEstudantes(responseEstudantes.data.estudantes || []);
        } else {
          setError('Instituição não encontrada no plano');
        }
      } catch (err) {
        console.error('Erro ao carregar dados:', err);
        setError(err.message || 'Erro ao carregar dados');
      } finally {
        setLoading(false);
      }
    };

    carregarDados();
  }, [planoId]);

  // Atualização do formulário
  const handleChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  // Submissão do formulário
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);

      const planoIndividual = await planosPagamentoService.criarPlanoIndividual({
        planoBaseId: planoId,
        ...formData
      });

      navigate(`/planos-pagamento/${planoIndividual.data.id}`);
    } catch (error) {
      console.error('Erro ao criar plano individual:', error);
      setError('Erro ao vincular estudante ao plano');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
        <Button
          sx={{ mt: 2 }}
          onClick={() => navigate('/planos-pagamento')}
        >
          Voltar para Planos
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Vincular Estudante ao Plano
      </Typography>

      <Grid container spacing={3}>
        {/* Detalhes do Plano Base */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Plano Base Selecionado
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography color="text.secondary">Descrição</Typography>
                  <Typography variant="body1">{planoBase.descricao}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography color="text.secondary">Ano Letivo</Typography>
                  <Typography variant="body1">{planoBase.anoLetivo}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography color="text.secondary">Valor Base</Typography>
                  <Typography variant="body1">{formatarMoeda(planoBase.valorTotal)}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography color="text.secondary">Prestações</Typography>
                  <Typography variant="body1">{planoBase.prestacoes?.length || 0}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Formulário de Vinculação */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Dados da Vinculação
              </Typography>

              <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <FormControl fullWidth required>
                      <InputLabel id="estudante-label">Estudante</InputLabel>
                      <Select
                        labelId="estudante-label"
                        label="Estudante"
                        value={formData.estudanteId}
                        onChange={handleChange('estudanteId')}
                      >
                        {estudantes.map(estudante => (
                          <MenuItem key={estudante.id} value={estudante.id}>
                            {estudante.nome}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      required
                      label="Valor Total"
                      value={formData.valorTotal}
                      onChange={handleChange('valorTotal')}
                      type="number"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">MZN</InputAdornment>
                        )
                      }}
                      helperText="Use o valor base ou defina um valor personalizado"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Observações"
                      value={formData.observacoes}
                      onChange={handleChange('observacoes')}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                      <Button
                        variant="outlined"
                        onClick={() => navigate('/planos-pagamento')}
                      >
                        Cancelar
                      </Button>
                      <Button
                        type="submit"
                        variant="contained"
                      >
                        Vincular Estudante
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </form>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default VincularEstudantePage;
